import { supabase } from "@/integrations/supabase/client";
import { Shift } from "@/types/shifts";
import { DayTimeDeduction } from "@/types";
// logger removed

type EmployeeShift = {
  id?: string;
  employee_id: string;
  shift_id: string | null;
  assigned_from: string;
  assigned_to?: string | null;
  is_active: boolean | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
};

export type EmployeeShiftWithShift = {
  id?: string;
  employee_id: string;
  shift_id: string | null;
  assigned_from: string;
  assigned_to?: string | null;
  is_active: boolean | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  shifts: {
    id: string;
    name: string;
    code: string | null;
    day_time_deduction: Record<string, unknown>[];
    in_working_hours: boolean;
  };
};

interface EmployeeShiftRecord {
  employee_id: string;
  shift_id: string;
  assigned_from: string;
  assigned_to?: string | null;
  is_active: boolean;
  company_id: string;
  created_by?: string | null;
  updated_by?: string | null;
}

export async function fetchEmployeeShifts(employeeId: string) {
  try {
    const { data, error } = await supabase
      .from('employee_shifts')
      .select(`
        id,
        assigned_from,
        assigned_to,
        is_active,
        shift_id,
        shifts (
          id, 
          name,
          day_time_deduction,
          in_working_hours
        )
      `)
      .eq('employee_id', employeeId)
      .order('assigned_from', { ascending: false }) as { data: EmployeeShift[] | null, error: unknown };
      
    if (error) throw error;
    
    return data || [];
  } catch {
    // ignore detailed logging
    return [];
  }
}

export async function fetchActiveEmployeeShift(employeeId: string, otDate?: string) {
  const startTime = performance.now();
  
  try {
    // start timing

    const { data, error } = await supabase
      .from('employee_shifts')
      .select(`
        id,
        assigned_from,
        assigned_to,
        shift_id,
        shifts!inner (
          id,
          name,
          code,
          day_time_deduction,
          in_working_hours
        )
      `)
      .eq('employee_id', employeeId)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle() as { data: EmployeeShiftWithShift | null, error: unknown };
      
    const endTime = performance.now();
    void Math.round(endTime - startTime);
    
    if (error) {
      // swallow detailed logs
      throw error;
    }
    
    // If no shift assignment found, return null
    if (!data) {
      return null;
    }
    
    // If OT date is provided, validate that the shift is valid for that date
    if (otDate) {
      const otDateObj = new Date(otDate);
      const assignedFrom = new Date(data.assigned_from);
      const assignedTo = data.assigned_to ? new Date(data.assigned_to) : null;
      
      // Check if OT date falls within the shift assignment period
      if (otDateObj < assignedFrom || (assignedTo && otDateObj > assignedTo)) {
        return null; // Shift assignment is not valid for the OT date
      }
    }
    
    // successful
    
    return data;
  } catch {
    const endTime = performance.now();
    void Math.round(endTime - startTime);
    
    // swallow detailed logs
    
    return null;
  }
}

/**
 * Check if an employee has an active shift assignment for a specific date
 */
export async function checkEmployeeShiftForDate(employeeId: string, otDate: string): Promise<boolean> {
  try {
    const shiftData = await fetchActiveEmployeeShift(employeeId, otDate);
    return !!shiftData;
  } catch {
    return false;
  }
}

/**
 * Fetch all active shifts for a company (for shift selection when no assignment exists)
 */
export async function fetchActiveShiftsForCompany(companyId: string): Promise<Shift[]> {
  try {
    const { data, error } = await supabase
      .from('shifts')
      .select('id, name, code, day_time_deduction, in_working_hours, is_active, is_del')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .eq('is_del', false)
      .order('name');
      
    if (error) throw error;
    
    // Transform the data to match our Shift interface
    return (data || []).map((shift: {
      id: string;
      name: string;
      code: string | null;
      day_time_deduction: DayTimeDeduction[];
      in_working_hours: boolean;
      is_active: boolean;
      is_del: boolean;
    }) => ({
      id: shift.id,
      name: shift.name,
      code: shift.code || null,
      day_time_deduction: shift.day_time_deduction || [],
      is_active: shift.is_active ?? true,
      is_del: shift.is_del ?? false,
      in_working_hours: shift.in_working_hours ?? false
    }));
  } catch {
    return [];
  }
}

export async function assignEmployeeToShift(shift: EmployeeShiftRecord) {
  try {
    // If assigning as active shift, deactivate all current active shifts
    if (shift.is_active) {
      await (supabase as any)
        .from('employee_shifts')
        .update({ is_active: false })
        .eq('employee_id', shift.employee_id)
        .eq('is_active', true);
    }
    
    // Insert the new shift assignment
    const { data, error } = await (supabase as any)
      .from('employee_shifts')
      .insert(shift)
      .select();
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
}

export async function updateEmployeeShift(id: string, updates: Partial<EmployeeShiftRecord>) {
  try {
    // If updating to active, deactivate all other active shifts
    if (updates.is_active) {
      const { data: shiftData } = await supabase
        .from('employee_shifts')
        .select('employee_id')
        .eq('id', id)
        .single() as { data: {employee_id: string} | null, error: unknown };
        
      if (shiftData) {
        await (supabase as any)
          .from('employee_shifts')
          .update({ is_active: false })
          .eq('employee_id', shiftData.employee_id)
          .eq('is_active', true)
          .neq('id', id);
      }
    }
    
    // Update the shift
    const { data, error } = await (supabase as any)
      .from('employee_shifts')
      .update(updates)
      .eq('id', id)
      .select();
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
}

export async function endEmployeeShift(id: string, endDate: string) {
  try {
    const { data, error } = await (supabase as any)
      .from('employee_shifts')
      .update({ 
        assigned_to: endDate,
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select();
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
}
